import Analytics from '@/mixins/Analytics';
import eventNames from '@/constants/analyticsEventNames';

import redirectAppStoreIfMobile from '@/utilities/redirectAppStoreIfMobile';
import importFederation from '@/utilities/mfe/importFederation';
import { withAbsoluteRedirects } from '@/utilities/mfe/mfeRouting';
import { store } from '@/appContext';
import applyDealerIdQueryString from '@/utilities/myacv/applyDealerIdQueryString';
import {
  registrationRoute,
  registrationV2Route,
} from './registrationRoutes';
import myAcvRoutes from './myAcv.routes';
import transportationHubRoutes from './transportationHub.routes';

/** Provide capital routes from CPL as a fallback in case remote is unavailable */
const capitalRoutes = await importFederation('capital/routes')
  .then((data) => data.default)
  .catch((e) => {
    Analytics.methods.noticeError('CAPITAL ROUTES FAILED', e);
    return [];
  });

async function getMyAcvSendToAuctionComponent() {
  // Unwrap ES module first to avoid a circular-dep TDZ error in Vue Router
  const myAcvSendToAuctionView = await import('@/views/MyAcv/MyInventoryMfeInterface.vue');

  return myAcvSendToAuctionView.default;
}

const routes = [
  {
    path: '/',
    redirect: 'login',
  },
  {
    path: '/universal-links/savedAuctions',
    redirect(to) {
      if (to.query.savedAuctionId) {
        return redirectAppStoreIfMobile(`/saved/${to.query.savedAuctionId}`);
      }
      return redirectAppStoreIfMobile('/myacv/saved/');
    },
  },
  {
    path: '/universal-links/search',
    redirect(to) {
      if (to.query.auctionId) {
        return redirectAppStoreIfMobile(`/auction/${to.query.auctionId}`);
      }
      return redirectAppStoreIfMobile('/auction/');
    },
  },
  {
    path: '/marketplace/:auctionDetailAuctionId?',
    name: 'Marketplace',
    component: () => import('@/views/Marketplace/Marketplace.vue'),
    meta: {
      title: 'Marketplace',
      showFooter: false,
      useLightUi: false,
      hasMobileTopBarHeader: true,
      allowAuctionCommonModals: true,
    },
    props: (route) => {
      const auctionDetailAuctionId = Number.parseInt(
        route.params.auctionDetailAuctionId,
        10,
      );
      return {
        auctionDetailAuctionId: !Number.isNaN(auctionDetailAuctionId)
          ? auctionDetailAuctionId
          : undefined,
      };
    },
    beforeEnter(to, from, next) {
      // when provided an id and there was no from path, this was a direct navigation
      // which should navigate to the auction route for that id instead
      if (to.params.auctionDetailAuctionId && (from.path === '/' || from.name === 'login')) {
        next({
          name: 'auction',
          params: {
            id: to.params.auctionDetailAuctionId,
          },
          query: to.query,
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/search/:auctionDetailAuctionId?',
    name: 'Search',
    redirect: (to) => {
      // redirect classic SRP to Marketplace SRP routes
      const auctionDetailAuctionId = Number.parseInt(
        to.params.auctionDetailAuctionId,
        10,
      );
      return Number.isNaN(auctionDetailAuctionId)
        ? { name: 'Marketplace' }
        : { name: 'Marketplace', params: { auctionDetailAuctionId } };
    },
  },
  {
    path: '/jwt-embed',
    name: 'JWTEmbed',
    component: () => import('@/views/Jwt/JwtEmbed.vue'),
    meta: {
      title: 'JWT Embed',
      allowUnauthorized: true,
      showHeader: false,
      showFooter: false,
      useLightUi: true,
      allowableRoutes: {
        MyAcvActiveBuying: '/myacv/active/buying',
        MyAcvSellerPerformance: '/myacv/performance',
        MyAcvInvoices: '/myacv/billing/invoices',
        PendingProxy: '/myacv/pendingproxy',
        Saved: '/myacv/saved',
        MarketReport: '/market-report',
        RequestInspectionNew: '/myacv/request-inspection/new',
        CapitalPortal: '/capital',
        Inventory: '/myacv/myinventory',
        MyAcvGuaranteedSale: '/myacv/guaranteed-sale',
        TransportationHub: '/transportation-hub',
        TransportationHubQuote: '/transportation-hub?view=quote',
        TransportationHubGetQuote: '/transportation-hub/get-quote',
        Acquisitions: '/myacv/myinventory/acquisitions',
      },
    },
  },
  {
    path: '/titles',
    name: 'MyAcvDigitalClerking',
    component: () => import('@/views/DigitalClerking/AcvDigitalClerk.vue'),
    redirect: '/titles/open',
    meta: {
      showHeader: true,
      shortTitle: 'Title Hub',
      simplifiedHeader: true,
      logoAlt: 'Title Hub Logo',
      showFooter: true,
      useLightUi: false,
      title: 'Title Hub',
      description: 'Manage open and closed titles',
      sellerOnly: true,
      displayName: 'Title Hub',
      hasExport: false,
      divider: true,
    },
    children: [
      {
        name: 'OpenItems',
        path: 'open',
        component: () => import('@/views/DigitalClerking/OpenItems.vue'),
        props: {
          viewType: 'open',
        },
        meta: {
          showHeader: true,
          simplifiedHeader: true,
          logoAlt: 'Title Hub Logo',
          hideFromView: true,
          showFooter: true,
          useLightUi: true,
        },
      },
      {
        name: 'ClosedItems',
        path: 'closed',
        component: () => import('@/views/DigitalClerking/ClosedItems.vue'),
        props: {
          viewType: 'closed',
        },
        meta: {
          showHeader: true,
          simplifiedHeader: true,
          logoAlt: 'Title Hub Logo',
          hideFromView: true,
          showFooter: true,
          useLightUi: true,
        },
      },
      {
        name: 'PreSaleItems',
        path: 'pre-sale',
        component: () => import('@/views/DigitalClerking/PreSaleItems.vue'),
        async beforeEnter(to, from, next) {
          const showPreSaleTitles = await store.dispatch(
            'getFlag',
            store.state.featureFlags.featureFlagSlugs.showPreSaleTitles,
          );

          if (!showPreSaleTitles) {
            next({ name: 'MyAcvDigitalClerking' });
          }

          next();
        },
        props: {
          viewType: 'pre-sale',
        },
        meta: {
          showHeader: true,
          simplifiedHeader: true,
          logoAlt: 'Title Hub Logo',
          hideFromView: true,
          showFooter: true,
          useLightUi: true,
        },
      },
    ],
  },
  {
    path: '/market-report',
    name: 'MarketReport',
    component: () => import('@/views/MarketReport/MarketReport.vue'),
    props: () => ({
      user: store.getters.currentUser,
      auctionLinksRedirect: !!window?.acvNative,
    }),
    meta: {
      title: 'Market Report',
      hasMobileTopBarHeader: true,
    },
  },
  {
    path: '/market-report/embed',
    name: 'MarketReportEmbed',
    component: () => import('@/views/MarketReport/MarketReportEmbedWrapper.vue'),
    meta: {
      allowUnauthorized: true,
      showHeader: false,
      showFooter: false,
      title: 'Market Report',
    },
  },
  {
    path: '/auction-access',
    redirect: '/registration',
  },
  registrationRoute,
  registrationV2Route,
  {
    path: '/sso-redirect',
    component: () => import('@/views/Sso/SsoRedirect.vue'),
    title: 'Single Sign-On Redirect',
    meta: {
      showFooter: false,
      ssoRoute: true,
      allowUnauthorized: true,
    },
    async beforeEnter(to, from, next) {
      if (store.getters.isAuthorized) {
        next({
          path: '/myacv/request-inspection/new',
          replace: true,
        });
      } else {
        await store.dispatch('clearUser');
        next();
      }
    },
    children: [
      {
        name: 'SSO Authentication',
        path: '',
        component: () => import('@/views/Sso/SsoAuthentication.vue'),
        meta: {
          title: 'SSO Authentication',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: false,
          ssoRoute: true,
        },
      },
      {
        name: 'SSO Error',
        path: '/sso-error',
        component: () => import('@/views/Sso/SsoError.vue'),
        meta: {
          title: 'SSO Error',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: false,
          ssoRoute: true,
        },
      },
    ],
  },
  {
    name: 'password',
    path: '/password',
    component: () => import('@/views/Login/SetPassword.vue'),
    beforeEnter(to, from, next) {
      if (!to.query.token) {
        next({ path: '/login' });
      } else {
        next();
      }
    },
    meta: {
      title: 'Reset Password',
      allowUnauthorized: true,
      showFooter: false,
      showRegistrationHeaderButtons: false,
    },
  },
  {
    name: 'forgot',
    path: '/forgot',
    component: () => import('@/views/Login/ForgotPassword.vue'),
    meta: {
      title: 'Forgot Password',
      allowUnauthorized: true,
      showFooter: false,
      showRegistrationHeaderButtons: false,
    },
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    children: [
      {
        name: 'login',
        path: '',
        component: () => import('@/views/Login/LoginContent.vue'),
        meta: {
          title: 'Login',
          allowUnauthorized: true,
          showFooter: false,
        },
        async beforeEnter(to, from, next) {
          if (store.getters.refreshToken?.length > 0) {
            try {
              await store.dispatch('refreshJwtTokens');
            } catch (err) {
              // do nothing
            }
          }
          if (store.getters.isAuthorized) {
            next({
              name: 'Marketplace',
              replace: true,
              showFooter: false,
            });
          } else {
            await store.dispatch('clearUser');
            next();
          }
        },
      },
    ],
  },
  {
    path: '/auction/:id',
    name: 'auction',
    component: async () => {
      const usePandaMfe = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs
          .usePandaMfe,
      );

      return usePandaMfe
        ? import('@/views/Marketplace/MarketplaceVehicleDetailPage.vue')
        : import('@/views/Auction.vue');
    },
    props: (route) => ({
      id: Number.parseInt(route.params.id, 10),
      performAction: Object.prototype.hasOwnProperty.call(
        route.query,
        'action',
      ),
    }),
    meta: {
      title: 'Auction',
      showBackButton: true,
      allowAuctionCommonModals: true,
    },
  },
  {
    path: '/saved/:id',
    name: 'saved',
    component: () => import('@/views/Auction.vue'),
    async beforeEnter(to, from, next) {
      next(await applyDealerIdQueryString(to, from));
    },
    props: (route) => ({
      id: Number.parseInt(route.params.id, 10),
      performAction: Object.prototype.hasOwnProperty.call(
        route.query,
        'action',
      ),
    }),
    meta: {
      title: 'Saved Auction',
      allowAuctionCommonModals: true,
    },
  },
  {
    path: '/sale-event/:saleEventId',
    name: 'SaleEvent',
    component: () => import('@/views/Marketplace/MarketplaceSaleEventPage.vue'),
    async beforeEnter(to, from, next) {
      const usePandaMfeForRc = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs
          .usePandaMfeForRc,
      );
      if (usePandaMfeForRc) {
        next();
      } else {
        next({
          name: 'Marketplace',
          replace: true,
        });
      }
    },
    props: (route) => ({
      saleEventId: route.params.saleEventId,
    }),
    meta: {
      title: 'Sale Event',
      showBackButton: true,
      allowAuctionCommonModals: true,
    },
  },
  // TODO - Redirect to /settings/proxybids when settings gets released - ETC
  {
    path: '/proxybids',
    redirect: '/settings/proxybids',
  },
  {
    path: '/settings',
    component: () => import('@/views/Settings/Settings.vue'),
    children: [
      {
        name: 'Settings',
        path: '',
        redirect: '/settings/account',
      },
      {
        path: 'account',
        name: 'MyAccount',
        component: () => import('@/views/Settings/MyAccount.vue'),
        meta: {
          title: 'My Account',
          adminOnly: false,
        },
      },
      {
        path: 'addresses',
        name: 'MyAddresses',
        component: () => import('@/views/Settings/MyAddresses.vue'),
        meta: {
          title: 'Manage pickup and delivery addresses',
          adminOnly: true,
        },
        props: (route) => ({
          showSuccess: route.query.showSuccess === 'true',
          orgId: route.query.id && Number(route.query.id),
          orgName: route.query.name,
          addOrUpdate: route.query.addOrUpdate,
        }),
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'add-new-address',
        name: 'AddNewAddress',
        component: () => import('@/views/Settings/AddNewAddress.vue'),
        meta: {
          title: 'Add new address',
          adminOnly: true,
          sidebarHighlight: 'MyAddresses',
        },
        props: (route) => ({
          orgId: route.query.id && Number(route.query.id),
          orgName: route.query.name,
        }),
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'edit-address',
        name: 'EditAddress',
        component: () => import('@/views/Settings/EditAddress.vue'),
        meta: {
          title: 'Edit address',
          adminOnly: true,
          sidebarHighlight: 'MyAddresses',
        },
        props: (route) => ({
          orgId: route.query.id && Number(route.query.id),
          orgName: route.query.name,
          addressId: route.query.addressId && Number(route.query.addressId),
        }),
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'docs',
        name: 'RegistrationDocuments',
        component: () => import('@/views/Settings/RegistrationDocuments.vue'),
        meta: {
          title: 'Registration Documents',
          adminOnly: false,
        },
        async beforeEnter(to, from, next) {
          const dealerDocsUploadDisabled = await store.dispatch(
            'getFlag',
            store.state.featureFlags.featureFlagSlugs.dealerDocsUploadDisabled,
          );

          if (dealerDocsUploadDisabled) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'paymentmethod',
        name: 'Paymentmethod',
        component: () => import('@/views/Settings/PaymentMethod.vue'),
        meta: {
          title: 'Payment Method Settings',
          adminOnly: true,
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'dealership',
        name: 'MyDealership',
        component: () => import('@/views/Settings/MyDealership.vue'),
        meta: {
          title: 'My Dealership',
          adminOnly: true,
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'dashboard',
        name: 'ManageUsers',
        component: () => import('@/views/Settings/ManageUsers.vue'),
        meta: {
          title: 'Manage Users',
          adminOnly: true,
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'proxybids',
        name: 'ProxyBidSettings',
        component: () => import('@/views/Settings/ProxyBidSettings.vue'),
        meta: {
          title: 'Proxy Bid Settings',
          adminOnly: false,
        },
      },
      {
        path: 'notification-settings',
        name: 'NotificationSettings',
        component: () => import('@/views/Settings/NotificationSettings.vue'),
        meta: {
          adminOnly: false,
        },
      },
      {
        path: 'sms-settings',
        name: 'SmsOptInSettings',
        component: () => import('@/views/Settings/SmsOptInSettings.vue'),
        meta: {
          adminOnly: false,
        },
      },
    ],
  },
  // Keeping routes in case any external links are pointed
  {
    path: '/register',
    redirect: (to) => ({
      path: '/registration',
      query: to.query,
    }),
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/Notifications.vue'),
    meta: {
      title: 'My Notifications',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/Logout.vue'),
    meta: {
      allowUnauthorized: true,
    },
  },
  {
    path: '/privacypolicy',
    name: 'dnsform',
    component: () => import('@/views/DnsForm.vue'),
    meta: {
      allowUnauthorized: true,
      title: 'Privacy Policy',
    },
  },
  {
    path: '/finalize',
    beforeEnter: (to, from, next) => {
      const auctionId = to.query.auction_id;
      next({
        path: `/auction/${auctionId}`,
        replace: true,
        query: { action: null },
      });
    },
  },
  {
    path: '/carfax-signin',
    name: 'CarfaxSigninCallBack',
    component: () => import('@/views/CarfaxSigninCallback/CarfaxSigninCallback.vue'),
    props: (route) => {
      const stateArray = route.query?.state?.split(',') ?? [];
      const [state, vin] = stateArray;
      const code = route.query?.code;
      return {
        state,
        code,
        vin,
      };
    },
  },
  {
    path: '/offboarding',
    name: 'Offboarding',
    component: () => import('@/views/Offboarding/Offboarding.vue'),
    beforeEnter(to, from, next) {
      if (store.getters.currentUser.active) {
        next();
      } else {
        next({ path: '/settings/account' });
      }
    },
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
  },
  {
    path: '/myacv/send-to-auction',
    name: 'MyAcvSendToAuction',
    component: getMyAcvSendToAuctionComponent,
    props: (route) => ({
      context: route.meta?.context ?? 'send-to-auction',
    }),
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
      context: 'send-to-auction',
    },
  },
  {
    path: '/myacv/gsale-performance',
    name: 'MyAcvGsalePerformanceTable',
    component: () => import('@/views/MyAcv/MyAcvGsalePerformanceTable.vue'),
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
    beforeEnter(to, from, next) {
      if (store.getters.currentUser.active) {
        next();
      } else {
        next({ path: '/myacv/myinventory' });
      }
    },
  },
  {
    path: '/myacv/guaranteed-sale',
    name: 'MyAcvGuaranteedSale',
    component: () => import('@/views/MyAcv/MyAcvGuaranteedSale.vue'),
    async beforeEnter(to, from, next) {
      const myInventoryAddGuaranteedSaleOffers = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.myInventoryAddGuaranteedSaleOffers,
      );
      if (store.getters.currentUser.active
        && (myInventoryAddGuaranteedSaleOffers)) {
        next();
      } else {
        next({ path: '/myacv/myinventory' });
      }
    },
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
  },
  {
    path: '/capital',
    name: 'Capital',
    component: () => import('@/views/CapitalPortal/CapitalPortal.vue'),
    children: [
      ...withAbsoluteRedirects(capitalRoutes, '/capital'),
    ],
    meta: {
      title: 'Capital Portal',
      shortTitle: 'Capital Portal',
      description: 'ACV Capital Finance',
      linkText: 'Capital Portal',
      useLightUi: true,
    },
    async beforeEnter(to, from, next) {
      // Checking if isCapitalUser is defined in the user object
      const isCapitalUserDefined = Object.prototype.hasOwnProperty.call(store.getters.currentUser, 'isCapitalUser');

      if (from.name === 'login' || !isCapitalUserDefined || store.getters.currentUser.capitalUserFailed) {
        await store.dispatch('setIsCapitalUser');
        Analytics.methods.track(eventNames.capital.portal.existsRetry, {
          status: store.getters.currentUser.capitalUserFailed ? 'failed' : 'success',
          from: 'ACV Capital Tab',
        });
      }

      const user = store.getters.currentUser;

      if (user?.capitalUserFailed) {
        next({ path: '/capital/portal-error' });
        return;
      }

      const dealerIdQuery = Number(to.query.dealer_id);

      const capitalShowAdvantageLeadGen = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.capitalShowAdvantageLeadGen,
      );

      const { isCapitalUser } = store.getters.currentUser;

      const isCapitalWithFloorplanDealers = isCapitalUser
        && user.capitalDealerSelectionList.length > 0;

      const isCapitalAdvantageOnly = capitalShowAdvantageLeadGen
        && isCapitalUser
        && !isCapitalWithFloorplanDealers
        // This check if all dealers in the list of capital dealers have only ADVANTAGE product line
        && Object.entries(user?.capitalUser?.dealerships)
          ?.every(([, value]) => value?.product_lines?.buyer?.every((product) => product === 'ADVANTAGE'));

      if (dealerIdQuery && store.getters.isAuthorized) {
        if (isCapitalWithFloorplanDealers) {
          Analytics.methods.track(
            eventNames.capital.landing.salesRepURLAccessed,
            {
              user_id: user.user_id,
              dealer_id: dealerIdQuery,
              error: 'The dealer is capital',
            },
          );
        }
      }

      if (!isCapitalUser || isCapitalAdvantageOnly) {
        next({ path: '/capital/landing-page' });
      } else if (!isCapitalWithFloorplanDealers) {
        next({ path: '/marketplace' });
      } else {
        next();
      }
    },
  },
  {
    path: '/capital/landing-page',
    name: 'NonCapitalUserLandingPage',
    component: () => import('@/views/CapitalPortal/NonCapitalUserLandingPage.vue'),
    async beforeEnter(to, from, next) {
      if (store.getters.currentUser?.capitalUserFailed) {
        await store.dispatch('setIsCapitalUser');
        Analytics.methods.track(eventNames.capital.portal.existsRetry, {
          status: store.getters.currentUser.capitalUserFailed ? 'failed' : 'success',
          from: 'URL Landing Page',
        });
      }

      const capitalShowAdvantageLeadGen = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.capitalShowAdvantageLeadGen,
      );

      const user = store.getters.currentUser;

      const { isCapitalUser } = store.getters.currentUser;

      const isCapitalWithFloorplanDealers = isCapitalUser
        && user.capitalDealerSelectionList.length > 0;

      const isCapitalAdvantageOnly = capitalShowAdvantageLeadGen
        && isCapitalUser
        && !isCapitalWithFloorplanDealers
        // This check if all dealers in the list of capital dealers have only ADVANTAGE product line
        && Object.entries(user?.capitalUser?.dealerships)
          ?.every(([, value]) => value?.product_lines?.buyer?.every((product) => product === 'ADVANTAGE'));

      if (user?.capitalUserFailed) {
        next({ path: '/capital/portal-error' });
      } else if (isCapitalWithFloorplanDealers) {
        next('/capital');
      } else if (!isCapitalUser || isCapitalAdvantageOnly) {
        next();
      } else {
        next({ path: '/marketplace' });
      }
    },
    meta: {
      useLightUI: true,
    },
  },
  {
    path: '/capital/portal-error',
    name: 'CapitalPortalError',
    component: () => import('@/views/CapitalPortal/CapitalPortalError.vue'),
    beforeEnter(to, from, next) {
      const user = store.getters.currentUser;

      if (user?.capitalUserFailed) {
        next();
      } else {
        next({ path: '/marketplace' });
      }
    },
    meta: {
      useLightUI: true,
    },
  },
  // Keeping route in case any external links are still pointing to credit-balance
  {
    path: '/capital/credit-balance',
    redirect: '/capital/account-details',
  },
  // Keeping route in case any external links are still pointing to portfolio
  {
    path: '/capital/portfolio',
    redirect: '/capital/account-details',
  },
  {
    path: '/transportation-hub',
    component: () => import(
      /* webpackChunkName: "Transportation" */ '@/views/TransportationHub/TransportationHub.vue'
    ),
    name: 'transportation-hub',
    meta: {
      title: 'Transportation Hub',
    },
  },
  {
    path: '/rewards',
    component: () => import('@/views/Loyalty/LoyaltySignup.vue'),
    async beforeEnter(to, from, next) {
      await store.dispatch('checkEligibilityToRewardsProgram');

      if (store.state.rewards.isRewardsProgramEligible) {
        next();
      } else {
        await store.dispatch('setRewardsPageViewDenied', true);
        next({ path: '/' });
      }
    },
  },
  {
    path: '/request-cash-flow/expired',
    name: 'RequestCashFlowExpired',
    component: () => import('@/views/CapitalPortal/RequestCashFlowExpired.vue'),
    meta: {
      title: 'Capital Finicity Request Expired',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/request-cash-flow',
    name: 'AccessFinicityPortal',
    component: () => import('@/views/CapitalPortal/AccessFinicityPortal.vue'),
    meta: {
      title: 'Capital Finicity Request Cash Flow',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/finicity-successfully-connected',
    name: 'FinicitySuccessfullyConnected',
    component: () => import('@/views/CapitalPortal/FinicitySuccessfullyConnected.vue'),
    meta: {
      title: 'Capital Finicity Request Successfully Connected',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/request-cash-flow/updated-link',
    name: 'RequestCashFlowUpdatedLink',
    component: () => import('@/views/CapitalPortal/RequestCashFlowUpdatedLink.vue'),
    meta: {
      title: 'Capital Finicity Request Updated Link',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/finicity-error-handler',
    name: 'FinicityErrorHandler',
    component: () => import('@/views/CapitalPortal/FinicityErrorHandler.vue'),
    meta: {
      title: 'Capital Finicity Error Handler',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  // This route to catch-all and redirect to a landing page at the end
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login',
  },
].concat(myAcvRoutes, transportationHubRoutes);

export default routes;
